import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { FaCarAlt, FaPlusCircle, FaWallet } from "react-icons/fa";
import api from "../../setupMiddleware";
import useApiLoader from "../../Utils/useApiLoader";
import Pagination from "../Common/Pagination";
import Loader from "../Loader/Loader";

export default function Transactions() {
  const loading = useApiLoader();
  const [transactions, setTransactions] = useState([]);

  // Pagination State
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const fetchTransaction = async () => {
    try {
      const res = await api.get("/api/transactions");
      if (res.status === 200) setTransactions(res.data?.transactions || []);
    } catch (err) {
      console.error("Error in fetching transactions", err);
    }
  };

  useEffect(() => {
    fetchTransaction();
  }, []);

  const totalPages = Math.max(1, Math.ceil(transactions.length / pageSize));

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [totalPages, page]);

  const paginatedTransactions = useMemo(() => {
    const start = (page - 1) * pageSize;
    return transactions.slice(start, start + pageSize);
  }, [transactions, page, pageSize]);

  return (
    <>
      {loading && <Loader opacity={50} />}

      <div className="text-gray-800 relative">
        <main className="max-w-5xl mx-auto py-3 px-3">
          <div className="bg-white/95 backdrop-blur-md border border-gray-100 rounded-2xl shadow-[inset_0_1px_1px_rgba(255,255,255,0.8),_0_2px_6px_rgba(0,0,0,0.05)] mt-1 md:mt-6 pb-4 overflow-hidden">
            {/* Header */}
            <div className="flex justify-between items-center px-5 py-5 bg-gray-50/60 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-brand/10 text-brand shadow-inner">
                  <FaWallet className="w-4 h-4" />
                </div>
                <h2 className="font-semibold text-gray-800 text-xs md:text-sm tracking-wide">
                  My Transactions
                </h2>
              </div>

              {/* Row Count */}
              <div className="flex items-center gap-2 text-xs md:text-sm">
                <span className="text-xs text-gray-500">Rows:</span>
                <select
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  className="border rounded px-2 py-1 text-xs md:text-sm bg-white"
                >
                  {[5, 10, 20, 50].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Transaction List */}
            <div className="p-5 divide-y divide-gray-100">
              {paginatedTransactions.length > 0 ? (
                paginatedTransactions.map((tx) => (
                  <div
                    key={tx.id}
                    className="flex justify-between items-center py-3 hover:bg-gray-50 rounded-lg px-2"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-9 h-9 flex items-center justify-center rounded-xl ${
                          tx.type === "credit"
                            ? "bg-green-100 text-green-600"
                            : "bg-rose-100 text-rose-600"
                        } shadow-inner`}
                      >
                        {tx.type === "credit" ? (
                          <FaPlusCircle className="w-5 h-5" />
                        ) : (
                          <FaCarAlt className="w-5 h-5" />
                        )}
                      </div>

                      <div>
                        <p className="font-medium text-gray-800 text-xs md:text-sm">
                          {tx.type === "debit"
                            ? `${tx.request_type} ${tx.transaction_name}`
                            : tx.transaction_name}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {moment(tx.transaction_time).format(
                            "DD MMM YYYY, hh:mm A"
                          )}
                        </p>
                      </div>
                    </div>

                    <span
                      className={`font-semibold text-xs md:text-sm px-3 py-1.5 rounded-full ${
                        tx.type === "credit"
                          ? "text-green-700 bg-green-50"
                          : "text-rose-600 bg-rose-50"
                      }`}
                    >
                      {tx.type === "credit"
                        ? `+ ₹${Number(tx.amount).toLocaleString("en-IN")}`
                        : `- ₹${Math.abs(tx.amount).toLocaleString("en-IN")}`}
                    </span>
                  </div>
                ))
              ) : (
                <div className="text-center py-5 text-gray-400 text-xs md:text-sm">
                  No recent transactions
                </div>
              )}
            </div>

            {/* Pagination */}
            <Pagination current={page} total={totalPages} onChange={setPage} />
          </div>
        </main>
      </div>
    </>
  );
}
